<template>
    <v-list-item link :to="route" color="accent" :prepend-icon="prepend_icon">
        <v-list-item-title class="text-subtitle-2">
            {{ dname }}
            <IconRevision :rev="rev" :old="oldrev" />
            <span class="mx-1 pit-mono"><strong>{{ constructible.priority }}</strong>/{{ max_batch_priority }}</span>
            <v-chip v-if="multipanel_group" size="x-small" density="compact" variant="tonal" color="purple" class="mx-1 multipanel-chip">
                <v-icon size="x-small" start>mdi-set-merge</v-icon> {{ multipanel_group.name }}
            </v-chip>
            <v-chip color="error" v-if="delayed" size="x-small">
                <v-icon size="small" start> mdi-clock-outline </v-icon> {{ constructible.scheduled_prod_date }}
            </v-chip>
            <span class="mx-1 font-weight-light text-grey text-caption pit-mono"> {{ constructible.uuid.slice(0, 8) }} </span>
        </v-list-item-title>
        <v-list-item-subtitle class="text-no-wrap">
            <v-chip size="x-small" variant="outlined" color="info"> {{ batch_chip }} </v-chip>
            <v-chip v-if="custom_hint" class="ml-1" size="x-small" variant="outlined" color="secondary"> {{ custom_hint
            }} </v-chip>
            <br />
            <v-chip v-if="status == 'ready'" variant="flat" size="x-small" color="success" class="mt-1">
                {{ t("ready") }}
            </v-chip>
            <template v-else>
                <!-- One label, one value. The kit is not a second signal beside
                     the status: it is one more stage of the one status the row
                     reports, and it sits where `waiting` used to, because a
                     panel missing 13 parts is not waiting its turn. Absent when
                     the company has kit tracking off, when the panel has no kit
                     at all, and once the kit is complete and work has started —
                     the station chips carry the row from there. -->
                <v-chip v-if="kit_label" variant="flat" size="x-small" class="mt-1" :color="kit_label.color">
                    {{ kit_label.text }}
                </v-chip>
                <v-chip :class="(i == 0 && !kit_label) ? 'mt-1' : 'ml-1 mt-1'" v-for="(sc, i) in station_chips"
                    :key="i" size="x-small" :variant="sc.outlined ? 'outlined' : 'flat'" :color="sc.color" class="pit-mono">
                    {{ sc.number }}
                </v-chip>
            </template>
            <!-- Every delivery state past `unknown`, not just `in_rack`. The
                 row used to go quiet again the moment a panel was delivered,
                 which read as "it came off the rack" rather than "it left". -->
            <ChipDeliveryStatus v-if="on_its_way" :model-value="delivery_status" variant="outlined"
                class="ml-1 mt-1" />
        </v-list-item-subtitle>
    </v-list-item>
</template>

<script>
import IconRevision from '../../renderers/IconRevision.vue';
import ChipDeliveryStatus from '../../renderers/ChipDeliveryStatus.vue';
import { today } from "/src/utils/time.js"
import { useFullPitStore } from "@/stores/app_common";
import { useAppStore } from '@/stores/app';
import { levelIcon } from '@/plugins/icons';
import { kitLabel } from '/src/utils/kits.js';
export default {
    setup() {
        const pit_store = useFullPitStore()
        return { ...pit_store }
    },
    components: {
        IconRevision,
        ChipDeliveryStatus,
    },
    props: ["constructible", "level"],
    computed: {
        // The entity glyph, always — an item is an assembly or a module and stays
        // one whatever it is doing (icons.md §2). It used to swap between a
        // calendar, a hammer, a check and a bookshelf, which made the row change
        // identity as it progressed. The state is already on the row: the ready
        // chip, the station chips and the in-rack chip, all in the subtitle.
        //
        // The spinner stays: that is not a status, it is this row loading.
        prepend_icon() {
            if (this.loading_this) return "mdi-loading mdi-spin"
            return levelIcon(this.level)
        },
        custom_hint() {
            return this.constructibles_store.list_hints[this.constructible.uuid] || ""
        },
        loading_this() {
            return this.app_store.active_loaders.includes("constructible_" + this.constructible.uuid)
        },
        design() {
            return this.constructibles_store.designsByUuid.get(this.constructible?.design)
        },
        batch() {
            return this.constructibles_store.batchesByUuid.get(this.constructible?.batch)
        },
        max_batch_priority() {
            const app_store = useAppStore()
            if (app_store.byDateModeActive) {
                return this.constructibles_store.maxPriorityByDate.get(this.constructible?.scheduled_prod_date || "") || 0
            } else {
                return this.constructibles_store.maxPriorityByBatch.get(this.constructible?.batch) || 0
            }
        },
        dname() {
            return this.design?.name || "---";
        },
        rev() {
            return this.design?.revision || 0;
        },
        batch_chip() {
            let batch_name = this.batch ? this.batch.name : "";
            let phase = this.batch ? this.batch.phase : "";
            return `${phase} | ${batch_name}`;
        },
        oldrev() {
            const max_rev = this.constructibles_store.maxRevisionByDesignName.get(this.dname) || 0
            return this.rev !== max_rev
        },
        status() {
            return this.constructible?.status || ""
        },
        // The single stage label of this row, or null when there is nothing to
        // say about the kit. See src/utils/kits.js for the composition.
        kit_label() {
            return kitLabel(this.constructible, this.session_store.kit_mode, this.t)
        },
        delivery_status() {
            return this.constructible?.delivery_status || "unknown"
        },
        on_its_way() {
            return this.delivery_status !== "unknown"
        },
        delayed() {
            if (!this.constructible.scheduled_prod_date) return false
            else if (this.constructible.scheduled_prod_date < today()) return true
            else return false
        },
        station_chips() {
            let stations = this.factories_store.getPLStations(this.session_store.selected_line)
            return stations.map((s, i) => {
                let sta_name = s.name ? s.name : "unknown";
                let status = this.constructible?.stations_status[sta_name];
                let color = "info";
                let outlined = true;
                if (status == "working") {
                    outlined = false;
                    color = "warning";
                } else if (status == "disabled") {
                    outlined = true;
                    color = "grey";
                } else if (status == "ready") {
                    outlined = false;
                    color = "success";
                }
                return { number: i + 1, color, outlined };
            })
        },
        route() {
            return `/viewer/${this.level}/${this.constructible?.uuid}`
        },
        multipanel_group() {
            if (this.level !== 'assembly') return null
            return this.constructibles_store.multipanelGroupByAssemblyUuid.get(this.constructible?.uuid) || null
        },
        const_uuid() {
            return this.constructible.uuid
        },
    },
    mounted() {
        this.constructibles_store.fetchListHint(this.const_uuid, this.level)
    },
}

</script>